import React, { createContext, useContext, useReducer, useEffect } from 'react';
import type { PLVProduct, CartItem } from '../types/plv.types';
import { calculateTotalPriceFromCart } from '../utils/pricingUtils';
import { useAuthStore } from '../../../stores/auth-store';

interface CartState {
  items: CartItem[];
  totalItems: number;
  totalHT: number;
}

type CartAction =
  | { type: 'ADD_ITEM'; payload: { product: PLVProduct; quantity: number } }
  | { type: 'REMOVE_ITEM'; payload: string }
  | { type: 'UPDATE_QUANTITY'; payload: { itemId: string; quantity: number } }
  | { type: 'CLEAR_CART' }
  | { type: 'LOAD_CART'; payload: CartItem[] };

interface CartContextType {
  state: CartState;
  addItem: (product: PLVProduct, quantity: number) => void;
  removeItem: (itemId: string) => void;
  updateQuantity: (itemId: string, quantity: number) => void;
  clearCart: () => void;
  isInCart: (productId: string) => boolean;
  getItemQuantity: (productId: string) => number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

function calculateTotals(state: CartState): CartState {
  const totalItems = state.items.length;
  const totalHT = state.items.reduce((sum, item) => {
    return sum + calculateTotalPriceFromCart(item.product, item.quantity);
  }, 0);

  return { ...state, totalItems, totalHT: Math.round(totalHT * 100) / 100 };
}

function cartReducer(state: CartState, action: CartAction): CartState {
  switch (action.type) {
    case 'ADD_ITEM': {
      const { product, quantity } = action.payload;
      const itemId = `${product.id}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const newItems = [...state.items, { id: itemId, product, quantity }];
      return calculateTotals({ ...state, items: newItems });
    }
    case 'REMOVE_ITEM': {
      const newItems = state.items.filter(item => item.id !== action.payload);
      return calculateTotals({ ...state, items: newItems });
    }
    case 'UPDATE_QUANTITY': {
      const { itemId, quantity } = action.payload;
      if (quantity <= 0) {
        const newItems = state.items.filter(item => item.id !== itemId);
        return calculateTotals({ ...state, items: newItems });
      }
      const newItems = state.items.map(item =>
        item.id === itemId ? { ...item, quantity } : item,
      );
      return calculateTotals({ ...state, items: newItems });
    }
    case 'CLEAR_CART':
      return { items: [], totalItems: 0, totalHT: 0 };
    case 'LOAD_CART': {
      const loadedItems = (action.payload || []).map((item: CartItem) => {
        if (!item.id) {
          return { ...item, id: `${item.product.id}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}` };
        }
        return item;
      });
      return calculateTotals({ ...state, items: loadedItems });
    }
    default:
      return state;
  }
}

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(cartReducer, { items: [], totalItems: 0, totalHT: 0 });
  const { currentUser } = useAuthStore();
  const companyId = currentUser?.societyId;

  // Load cart from localStorage when company ID is available
  useEffect(() => {
    if (companyId) {
      const cartKey = `plv_cart_${companyId}`;
      const savedCart = localStorage.getItem(cartKey);
      if (savedCart) {
        try {
          const cartItems = JSON.parse(savedCart);
          dispatch({ type: 'LOAD_CART', payload: cartItems });
        } catch {
          // Ignore invalid cart data
        }
      }
    }
  }, [companyId]);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    if (companyId) {
      const cartKey = `plv_cart_${companyId}`;
      localStorage.setItem(cartKey, JSON.stringify(state.items));
    }
  }, [state.items, companyId]);

  const addItem = (product: PLVProduct, quantity: number) =>
    dispatch({ type: 'ADD_ITEM', payload: { product, quantity } });

  const removeItem = (itemId: string) =>
    dispatch({ type: 'REMOVE_ITEM', payload: itemId });

  const updateQuantity = (itemId: string, quantity: number) =>
    dispatch({ type: 'UPDATE_QUANTITY', payload: { itemId, quantity } });

  const clearCart = () => dispatch({ type: 'CLEAR_CART' });

  const isInCart = (productId: string) =>
    state.items.some(item => item.product.id.toString() === productId);

  const getItemQuantity = (productId: string) =>
    state.items
      .filter(item => item.product.id.toString() === productId)
      .reduce((total, item) => total + item.quantity, 0);

  return (
    <CartContext.Provider value={{ state, addItem, removeItem, updateQuantity, clearCart, isInCart, getItemQuantity }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
